import { VerticalFlexbox } from "@src/components/layout/Flexbox";
import { Input } from "@src/components/ui/input";
import { Label } from "@src/components/ui/label";
import { cn } from "@src/lib/utils";
import { NaiveDate } from "@src/utils/types";
import { isValid } from "date-fns";
import { useField } from "formik";
import { useId } from "react";

export interface FormikDateInputProps {
  name: string;
  label?: string;
  required?: boolean;
  containerClassName?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
}

/**
 * Formik-integrated native date input.
 *
 * The Formik field stores a `NaiveDate | null` value. `NaiveDate` is always
 * midnight UTC, so converting to/from the native HTML date format (`yyyy-MM-dd`)
 * is lossless and timezone-safe.
 */
export const FormikDateInput = ({
  name,
  label,
  required,
  containerClassName,
  disabled,
  min,
  max,
}: FormikDateInputProps) => {
  const [field, meta, helpers] = useField<NaiveDate | null>(name);
  const id = useId();
  const error = meta.touched && meta.error ? String(meta.error) : undefined;

  // NaiveDate is always midnight UTC, so toISOString().slice(0, 10) is safe
  const stringValue =
    field.value instanceof Date && isValid(field.value)
      ? NaiveDate.toString(field.value)
      : "";

  return (
    <VerticalFlexbox gap="xs" className={cn("w-full", containerClassName)}>
      {label ? (
        <Label htmlFor={id} className="text-sm text-foreground">
          {label}
          {required ? (
            <span className="ml-0.5 text-destructive" aria-hidden>
              *
            </span>
          ) : null}
        </Label>
      ) : null}
      <Input
        id={id}
        type="date"
        value={stringValue}
        onChange={(event) => {
          const raw = event.target.value;
          if (!raw) {
            helpers.setValue(null);
            return;
          }
          const parsed = NaiveDate(raw);
          helpers.setValue(isValid(parsed) ? parsed : null);
        }}
        onBlur={() => helpers.setTouched(true)}
        name={field.name}
        required={required}
        disabled={disabled}
        min={min}
        max={max}
        aria-invalid={error ? "true" : undefined}
        className={cn(error && "border-destructive ring-destructive")}
      />
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
    </VerticalFlexbox>
  );
};
